import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowLeft, DownloadCloud, Search, Video } from 'lucide-react';
import { getAllArchivedVideos } from '../videoArchive';

function formatBytes(bytes) {
  if (!bytes) return '0 KB';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

// Its own page (not a toggled panel on History) because these are real
// video files rendered with boxes burned in - they deserve a proper
// file-browser feel (thumbnail tiles), not a cramped list row.
export default function SavedVideos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      try {
        const rows = await getAllArchivedVideos();
        if (active) setVideos(rows);
      } catch {
        toast.error('Could not open the local video archive.');
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return videos;
    return videos.filter((v) => (
      String(v.productId || '').toLowerCase().includes(q)
      || String(v.camera || '').toLowerCase().includes(q)
      || String(v.date || '').toLowerCase().includes(q)
    ));
  }, [videos, query]);

  const handleDownload = (video) => {
    const url = URL.createObjectURL(video.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = video.filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  return (
    <div className="page-grid">
      <section className="control-bar">
        <Link to="/history" className="ghost-btn">
          <ArrowLeft size={15} /> Back to History
        </Link>
        <div className="log-search">
          <Search size={14} />
          <input
            type="text"
            placeholder="Search ID, CAM, or Date…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </section>

      <section className="data-card">
        <div className="data-card__header">
          <h3>Historical Saved Videos</h3>
          <span className="video-tile-grid__count">{filtered.length} of {videos.length}</span>
        </div>

        {loading && <p className="empty-cell">Loading saved videos…</p>}

        {!loading && filtered.length === 0 && (
          <p className="empty-cell">
            {videos.length === 0
              ? 'No detected videos saved yet - run a clip through detection on the Live Dashboard and it will be saved here automatically.'
              : 'No saved videos match your search.'}
          </p>
        )}

        {!loading && filtered.length > 0 && (
          <div className="video-tile-grid">
            {filtered.map((v) => (
              <div key={v.id} className="video-tile">
                <div className="video-tile__thumb">
                  {v.thumbnail ? (
                    <img src={v.thumbnail} alt="" />
                  ) : (
                    <Video size={30} />
                  )}
                  <span className="video-tile__ext">
                    {((v.mimeType || 'video/webm').split('/')[1] || 'webm').split(';')[0]}
                  </span>
                </div>
                <div className="video-tile__body">
                  <strong title={v.filename}>{v.filename}</strong>
                  <span>{v.camera} · {v.date}</span>
                  <span>{v.className} · {formatBytes(v.blob?.size)}</span>
                </div>
                <button type="button" className="ghost-btn" onClick={() => handleDownload(v)}>
                  <DownloadCloud size={14} /> Download
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
